import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Car, Plane, Box, Zap, HeartPulse, Cog } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { CLIENTS, INDUSTRIES, PROJECTS } from "@/data/content";

const ICONS = { car: Car, plane: Plane, box: Box, zap: Zap, "heart-pulse": HeartPulse, cog: Cog };

const Clients = () => (
  <div>
    <PageHeader
      eyebrow="Our Clients · Industries · Projects"
      title="Trusted across India's precision manufacturers"
      subtitle="From automotive to aerospace, energy to bearings — we power the machines behind mission-critical production."
    />

    {/* Marquee — black text */}
    <section className="py-6 border-y border-slate-200 bg-white">
      <Marquee gradient gradientColor="#FFFFFF" speed={35} className="py-3">
        {CLIENTS.map((c) => <span key={c} className="font-display text-xl md:text-2xl text-slate-900 mx-8">{c}</span>)}
      </Marquee>
    </section>

    {/* Recent Machine Projects — latest at top */}
    <section className="py-8 md:py-12">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="mb-6">
          <span className="eyebrow">Recent Projects</span>
          <h2 className="mt-3 text-2xl md:text-3xl text-slate-900">Machines we've retrofitted &amp; reconditioned</h2>
          <p className="mt-2 text-dim text-sm max-w-2xl">Latest work at the top — a snapshot of the machines we've brought back to life for our clients.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.05}
              className="group bg-white border border-[#0055FF]/30 rounded-2xl overflow-hidden card-hover flex flex-col"
              data-testid={`project-${p.id}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] bg-[#0055FF] text-white font-semibold">#{String(i + 1).padStart(2, "0")}</span>
                <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] bg-white/95 text-slate-700 border border-slate-200">{p.industry}</span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-display text-base leading-tight text-slate-900" data-testid={`project-title-${p.id}`}>{p.title}</h3>
                <p className="mt-1 text-[11px] uppercase tracking-widest text-[#0055FF]">{p.client}</p>
                <p className="mt-2 text-xs text-dim leading-relaxed line-clamp-3 flex-1">{p.solution}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Industries */}
    <section className="py-8 md:py-12 border-t border-slate-200 bg-slate-50">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <span className="eyebrow">Industries We Serve</span>
        <h2 className="mt-3 text-2xl md:text-3xl mb-6 text-slate-900">Precision engineering across every sector</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INDUSTRIES.map((ind, i) => {
            const Icon = ICONS[ind.icon] || Cog;
            return (
              <Reveal key={ind.name} delay={i * 0.04} className="bg-white border border-[#0055FF]/30 rounded-2xl p-5 card-hover">
                <div className="w-10 h-10 rounded-lg bg-[#0055FF]/10 border border-[#0055FF]/25 flex items-center justify-center"><Icon className="w-5 h-5 text-[#0055FF]" /></div>
                <h3 className="mt-4 text-lg text-slate-900">{ind.name}</h3>
                <p className="mt-1 text-dim text-sm">{ind.desc}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section className="py-10 md:py-14 border-t border-slate-200 text-center">
      <Reveal>
        <h2 className="text-2xl md:text-3xl text-slate-900">Join our growing list of partners</h2>
        <Link to="/contact" className="btn-primary mt-6 !text-sm">Become a Client <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
    </section>
  </div>
);

export default Clients;
